import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getCategory } from "../../state/categoryState";
import { getProduct } from "../../state/productState";
import { getRestaurant } from "../../state/restaurantState";
import "./styles.css";
import Footer from "../../components/Footer";
import ProductModal from "../../components/ProductModal";
import LikeButton from "../../components/LikeButton";
import useGetImages from "../../components/useGetImages";
import MobresBacklink from "../../components/MobresBacklink";

const formatPrice = (value) => {
  const num = Number(String(value).replace(",", "."));
  if (Number.isNaN(num)) return `${value} ₺`;
  return `${num.toLocaleString("tr-TR", {
    minimumFractionDigits: num % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })} ₺`;
};

const MenuCategory = React.memo(({ category, id, active, onSelect }) => {
  const { image: uri } = useGetImages("category_pictures", id, category.CATEGORY);
  const ref = useRef(null);

  // Seçili çip yatay şeridin ortasına kaydırılır (sayfa dikeyde kıpırdamaz).
  useEffect(() => {
    const el = ref.current;
    const strip = el?.parentElement;
    if (!active || !strip) return;
    strip.scrollTo({
      left: el.offsetLeft - (strip.clientWidth - el.clientWidth) / 2,
      behavior: "smooth",
    });
  }, [active]);

  return (
    <button
      ref={ref}
      type="button"
      className={`mn-chip${active ? " is-active" : ""}`}
      onClick={() => onSelect(category.CATEGORY)}
    >
      {uri && <img src={uri} alt="" className="mn-chip-img" />}
      <span>{category.CATEGORY}</span>
    </button>
  );
});

const ProductCard = React.memo(({ product, id, onOpen }) => {
  // Sadece PATH "x" olan ürünün resmi vardır; diğerlerinde resim API'sine istek atılmaz.
  const photoPath = (product?.PATH || "").trim().toLowerCase();
  const hasPhoto = photoPath === "x";
  const { image: uri, isLoading } = useGetImages(
    "product_pictures",
    id,
    product?.URUN_KOD,
    { enabled: hasPhoto }
  );
  const [imgFailed, setImgFailed] = useState(false);

  if (!product) return null;

  // Görseli olmayan üründe iskelet yerine görselsiz, metin odaklı kart çizilir.
  const showImage = hasPhoto && uri && !imgFailed;
  const showSkeleton = hasPhoto && !uri && isLoading;
  const description = product?.URUN_ACIKLAMA
    ? product.URUN_ACIKLAMA.split(",").map((item) => item.trim()).filter(Boolean).join(", ")
    : "";

  return (
    <article
      className={`mn-card${showImage || showSkeleton ? "" : " mn-card--text"}`}
      onClick={() => onOpen(product)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen(product)}
    >
      <div className="mn-card-body">
        <h3 className="mn-card-title">{product?.URUN_ADI}</h3>
        {description && <p className="mn-card-desc">{description}</p>}
        <div className="mn-card-footer">
          {product?.FIYATI && <span className="mn-price">{formatPrice(product.FIYATI)}</span>}
          <LikeButton productId={product?.URUN_KOD} />
        </div>
      </div>

      {showImage && (
        <div className="mn-card-media">
          <img src={uri} alt={product?.URUN_ADI} loading="lazy" onError={() => setImgFailed(true)} />
        </div>
      )}
      {showSkeleton && <div className="mn-card-media mn-skeleton" aria-hidden="true" />}
    </article>
  );
});

const Menu = ({ id }) => {
  const categories = useSelector((state) => state.categories.category);
  const products = useSelector((state) => state.products.product);
  const restaurants = useSelector((state) => state.restaurants.restaurant);
  const dispatch = useDispatch();
  const location = useLocation();
  const firstCategory = new URLSearchParams(location.search).get("category");
  const [chooseCategory, setChooseCategory] = useState(firstCategory);
  const [scrolled, setScrolled] = useState(false);
  const [cartModal, setCartModal] = useState(false);
  const [productDetail, setProductDetail] = useState();

  useEffect(() => {
    const data1 = { path: id + "/categoryno.json" };
    const data2 = { path: id + "/productsno.json" };

    dispatch(getProduct(data2));
    dispatch(getCategory(data1));
    dispatch(getRestaurant(id)); // userCode parametresi ile çağır
  }, [dispatch, id]);

  useEffect(() => {
    setChooseCategory(firstCategory);
    window.scrollTo(0, 0);
  }, [firstCategory]);

  // URL'de kategori yoksa ilk kategori seçili sayılır.
  const activeCategory = chooseCategory || categories?.[0]?.CATEGORY;

  const productItems = useMemo(
    () => (Array.isArray(products) ? products.filter((p) => p.CATEGORY === activeCategory) : null),
    [products, activeCategory]
  );

  const selectCategory = useCallback((category) => {
    setChooseCategory(category);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const openProduct = useCallback((product) => {
    setProductDetail(product);
    setCartModal(true);
  }, []);

  // Kaydırınca header'a gölge verilir.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const restaurant = restaurants?.[0];

  return (
    <div className="mn-page">
      <header className={`mn-header${scrolled ? " is-scrolled" : ""}`}>
        <div className="mn-header-top">
          <Link to="/category" className="mn-back" aria-label="Kategorilere dön">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </Link>
          <div className="mn-header-titles">
            {restaurant?.FIRMA && <span className="mn-eyebrow">{restaurant.FIRMA}</span>}
            <h1 className="mn-title">Menü</h1>
          </div>
        </div>

        <nav className="mn-chips" aria-label="Kategoriler">
          {categories?.map((item) => (
            <MenuCategory
              key={item.CATEGORY}
              category={item}
              id={id}
              active={activeCategory === item.CATEGORY}
              onSelect={selectCategory}
            />
          ))}
        </nav>
      </header>

      <main className="mn-main">
        {productItems ? (
          <>
            <div className="mn-section-head">
              <h2>{activeCategory}</h2>
              <span>{productItems.length} ürün</span>
            </div>
            {productItems.length ? (
              <div className="mn-grid">
                {productItems.map((item, index) => (
                  <ProductCard key={item.URUN_KOD ?? index} product={item} id={id} onOpen={openProduct} />
                ))}
              </div>
            ) : (
              <p className="mn-empty">Bu kategoride henüz ürün yok.</p>
            )}
          </>
        ) : (
          <div className="mn-grid" aria-busy="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="mn-card mn-card--ghost">
                <div className="mn-card-body">
                  <div className="mn-skeleton mn-line" style={{ width: "60%" }} />
                  <div className="mn-skeleton mn-line" style={{ width: "90%" }} />
                  <div className="mn-skeleton mn-line" style={{ width: "30%" }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {!!restaurants?.length && <Footer res={restaurant} />}

      <MobresBacklink />

      {cartModal && (
        <ProductModal setCartModal={setCartModal} productDetail={productDetail} id={id} />
      )}
    </div>
  );
};

export default Menu;
